import React, { useEffect, useState, FC } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { logError } from '../../../toaster'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { useProjectUsersQuery } from '../api/use-project-users-query'
import { TypeAheadDropdown } from './type-ahead-dropdown'
import { transformFormDataToBugtrackerRequest } from '../../bugtrackers/api/build-api-payload-bugtracker-credentials'
import { BugtrackerConfig, ProjectUser } from '../api/shared'
import { formDefaultValues, JiraFormFieldTypes } from './jira-form-field-types'
import { HTTPError } from '../../../../api/fetch/decorate-fetch'
import { JiraIntegrationForm } from './jira-form-schema'

export const DefaultAssigneeField: FC<{ bugtracker?: BugtrackerConfig }> = ({
  bugtracker
}) => {
  const { formatMessage } = useIntl()
  const [userSearch, setUserSearch] = useState<string>('')
  const [dropdownHidden, setDropdownHidden] = useState<boolean>(true)
  const [textInput, setTextInput] = useState<string>('')

  const {
    state: { organizationUuid }
  } = useJiraIntegrationPage()

  const { getValues, watch } = useFormContext<JiraIntegrationForm>()
  const watchProject = watch(JiraFormFieldTypes.PROJECT)
  const { users } = useProjectUsersQuery(
    {
      organizationUuid,
      bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
      projectName:
        (typeof watchProject === 'object' ? watchProject.key : watchProject) ??
        '',
      userSearch,
      bugtrackerId: bugtracker?.bugtracker_id
    },
    {
      onError: (error: HTTPError) => {
        const errorMessage = error?.data?.messages?.[0]
        if (errorMessage) {
          // Replace with DS toast component once additional props are available
          logError(errorMessage, { dismissOnTimeout: false, timeOut: 30000 })
        }
      }
    }
  )

  useEffect(() => {
    if (users?.length) {
      if (userSearch) {
        setDropdownHidden(false)
      } else {
        setDropdownHidden(true)
        const selectedUser = users.find((user) => {
          // eslint-disable-next-line unicorn/prefer-ternary
          if (typeof value === 'object') {
            return user?.key === value.accountId
          } else {
            return user?.key === value.toString()
          }
        })
        setTextInput(
          selectedUser?.displayName ||
            (formDefaultValues.assignee as ProjectUser).displayName
        )
        onChange(selectedUser || formDefaultValues.assignee)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  useEffect(() => {
    // Clear out the default assignee field when we change projects
    setUserSearch('')
  }, [watchProject])

  const {
    field: { onChange, value }
  } = useController({ name: JiraFormFieldTypes.ASSIGNEE })
  const onInputChange = (e) => {
    setTextInput(e)
    setUserSearch(e)
  }

  const suggestionClicked = (user: ProjectUser) => {
    onChange(user)
    setTextInput(user.displayName)
    setDropdownHidden(true)
  }

  const outsideDropdownActivity = () => {
    // Set input back to previous value
    setTextInput(value?.displayName ?? '')
    setDropdownHidden(true)
  }

  const TextInputProps = {
    value: textInput,
    label: formatMessage({
      id: 'title_integrationsSettings_defaultAssignee'
    }),
    onChange: onInputChange,
    disabled: watchProject === undefined,
    onFocus: () => {
      setTextInput('')
      setDropdownHidden(false)
    },
    placeholder: formatMessage({
      id: 'placeholder_jiraUsers'
    })
  }

  return (
    <TypeAheadDropdown
      TextInputProps={TextInputProps}
      hidden={dropdownHidden}
      values={users?.filter((user) => !!user) ?? []}
      onSelectionClicked={suggestionClicked}
      onOutsideActivity={outsideDropdownActivity}
      itemToString={(user) => user?.displayName}
      itemToKey={(user) => `${user.accountId}-${user.displayName}`}
      testId="default-assignee"
    />
  )
}
